import { useState } from 'react';
import { useAppSync } from '../../contexts/AppSyncContext';
import { cn } from '../../lib/utils';
import { Bell, DoorOpen, CheckCircle2, Send, Calendar, CreditCard, X } from 'lucide-react';

const ICON_MAP = {
  door: DoorOpen, check: CheckCircle2, send: Send, calendar: Calendar, billing: CreditCard, default: Bell,
};
const SOURCE_LABEL = { lumina: 'Lumina', echo: 'Echo', revq: 'RevQ' };
const SOURCE_COLOR = { lumina: '#6b5ce7', echo: '#3b82f6', revq: '#10b981' };

const CrossAppNotifications = ({ currentApp }) => {
  const { notifications, getUnreadCount, markNotifRead, clearNotifications } = useAppSync();
  const [open, setOpen] = useState(false);
  const unread = getUnreadCount();
  const appNotifs = notifications.filter(n => n.target === currentApp || n.source === currentApp).slice(0, 20);

  return (
    <div className="relative" data-testid="cross-app-notifications">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="notif-bell-btn"
      >
        <Bell className="h-4 w-4 text-white/70" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notif-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-800">Notifications</p>
              <div className="flex items-center gap-2">
                {unread > 0 && <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full font-medium">{unread} new</span>}
                <button onClick={() => clearNotifications()} className="text-[11px] text-slate-400 hover:text-slate-600" data-testid="clear-notifs">Clear</button>
              </div>
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              {appNotifs.length === 0 ? (
                <div className="p-6 text-center"><p className="text-xs text-slate-400">No notifications</p></div>
              ) : (
                appNotifs.map(n => {
                  const Icon = ICON_MAP[n.icon] || ICON_MAP.default;
                  const isIncoming = n.target === currentApp;
                  const sourceColor = SOURCE_COLOR[n.source] || '#888';
                  const ago = getTimeAgo(n.time);
                  return (
                    <div
                      key={n.id}
                      className={cn("flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer", !n.read && "bg-blue-50/30")}
                      onClick={() => markNotifRead(n.id)}
                      data-testid={`notif-${n.id}`}
                    >
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${sourceColor}15` }}>
                        <Icon className="h-4 w-4" style={{ color: sourceColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: sourceColor }}>{SOURCE_LABEL[n.source]}</span>
                          <span className="text-[9px] text-slate-300">→</span>
                          <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: SOURCE_COLOR[n.target] || '#888' }}>{SOURCE_LABEL[n.target]}</span>
                        </div>
                        <p className="text-xs text-slate-700 leading-snug">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{ago}</p>
                      </div>
                      {!n.read && <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-1" />}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function getTimeAgo(time) {
  const now = Date.now();
  const diff = now - new Date(time).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

export default CrossAppNotifications;
